import styled from '@emotion/styled';
import { Modal, Spin } from 'antd';
import type React from 'react';
import { type FC, useEffect, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { t, tt } from 'src/lib/player/i18n';
import { type StarterType, useStore } from 'src/lib/player/store';
import { correctedNow } from 'src/lib/player/utils/clock';
import { RACE_VIDEO_GRACE_MS, raceVideoFileName } from 'src/lib/raceVideo';
import { IconDownload, IconSharePlatformAware, IconVideo } from '../../BasicComponents/Icons';
import { type RaceVideoState, useRaceVideo } from './useRaceVideo';

/** The clip as a file, which is what Instagram and other apps accept from the share sheet. */
async function fetchFile(url: string, name: string): Promise<File | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return new File([await res.blob()], name, { type: 'video/mp4' });
  } catch {
    // Without the file, Share falls back to the link and Download opens the video.
    return null;
  }
}

/** The file is fetched before the click: an await ahead of `navigator.share` uses up Safari's user activation. */
async function share(url: string, file: File | null, title: string) {
  try {
    if (file != null && navigator.canShare?.({ files: [file] })) {
      trackEvent('race_video_share', { method: 'file' });
      await navigator.share({ files: [file], title });
    } else {
      trackEvent('race_video_share', { method: 'link' });
      await navigator.share({ url, title });
    }
  } catch (err) {
    // Dismissing the sheet rejects with AbortError. That is not a failure, so stay quiet.
    if (!(err instanceof Error && err.name === 'AbortError')) {
      console.warn('Native share failed:', err);
    }
  }
}

function download(url: string, file: File | null) {
  trackEvent('race_video_download');
  if (file == null) {
    // A cross-origin link ignores `download`, so the browser opens the video instead.
    window.open(url, '_blank', 'noopener');
    return;
  }
  const objectUrl = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = file.name;
  link.click();
  // Some engines read the blob URL after click() returns.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

function message(state: RaceVideoState): string | null {
  switch (state.status) {
    case 'loading':
    case 'rendering':
      return t('raceVideo.rendering');
    case 'unavailable':
      return state.reason === 'dnf' ? t('raceVideo.unavailable.dnf') : t('raceVideo.unavailable');
    case 'notFinished':
      return t('raceVideo.notFinished');
    case 'error':
      return t('raceVideo.error');
    default:
      return null;
  }
}

const RaceVideoView: FC<{ eventId: string; starter: StarterType; title: string }> = ({
  eventId,
  starter,
  title,
}) => {
  const state = useRaceVideo(eventId, starter.id);
  const [file, setFile] = useState<File | null>(null);
  const url = state.status === 'ready' ? state.url : null;
  const name = raceVideoFileName(starter);

  useEffect(() => {
    if (url == null) return;
    let cancelled = false;
    void fetchFile(url, name).then((fetched) => {
      if (!cancelled) setFile(fetched);
    });
    return () => {
      cancelled = true;
    };
  }, [url, name]);

  if (state.status !== 'ready') {
    const waiting = state.status === 'loading' || state.status === 'rendering';
    return (
      <Hint data-testid="race-video-status">
        {waiting && <Spin size="large" data-testid="race-video-spinner" />}
        <div>{message(state)}</div>
      </Hint>
    );
  }
  return (
    <Wrapper>
      <Video
        src={state.url}
        controls
        playsInline
        autoPlay
        muted
        loop
        onLoadedData={() => trackEvent('race_video_ready')}
      />
      <Actions>
        {typeof navigator.share === 'function' && (
          <ActionButton type="button" onClick={() => share(state.url, file, title)}>
            <IconSharePlatformAware />
            <span>{t('raceVideo.share')}</span>
          </ActionButton>
        )}
        <ActionButton type="button" onClick={() => download(state.url, file)}>
          <IconDownload />
          <span>{t('raceVideo.download')}</span>
        </ActionButton>
      </Actions>
    </Wrapper>
  );
};

/** Opens a finished starter's race video; `icon` for the sidebar row, `button` for the stats tab. */
export const RaceVideoButton: FC<{ starter: StarterType; variant?: 'button' | 'icon' }> = ({
  starter,
  variant = 'button',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const event = useStore((state) => state.events[starter.eventIndex]);

  if (event == null || !event.options.hasRaceVideo) return null;
  if (correctedNow() < event.endTime + RACE_VIDEO_GRACE_MS) return null;

  const title = tt('raceVideo.title', starter.name ?? starter.startNumber ?? '');
  function open() {
    trackEvent('race_video_open');
    setIsOpen(true);
  }
  // Clicks inside the modal bubble through the portal too; the row behind must not see them.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only keeps clicks from the row behind
    <span onClick={stop} onKeyUp={stop}>
      {variant === 'icon' ? (
        <IconButton
          type="button"
          title={t('raceVideo.button')}
          aria-label={t('raceVideo.button')}
          data-testid="race-video-button"
          onClick={open}
        >
          <IconVideo />
        </IconButton>
      ) : (
        <button
          type="button"
          className="button"
          title={t('raceVideo.button')}
          data-testid="race-video-button"
          onClick={open}
        >
          <IconVideo />
        </button>
      )}
      <Modal
        open={isOpen}
        destroyOnHidden
        onCancel={() => setIsOpen(false)}
        title={title}
        footer={null}
        centered
        width={420}
      >
        {isOpen && <RaceVideoView eventId={event.eventId} starter={starter} title={title} />}
      </Modal>
    </span>
  );
};

/* Bare, so the row's control styles size and colour the icon like the star and eye beside it. */
const IconButton = styled.button`
  display: flex;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Video = styled.video`
  width: 100%;
  max-height: 70vh;
  aspect-ratio: 9 / 16;
  background: black;
  border-radius: 4px;
`;

const Hint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 0;
  text-align: center;
  color: var(--text-color);
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

/* Real buttons, so Safari gets an activation source it can anchor the share sheet to. */
const ActionButton = styled.button`
  display: flex;
  flex: 1;
  gap: 8px;
  justify-content: center;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid var(--base-color-50);
  border-radius: 4px;
  background: white;
  color: var(--text-color);
  font: inherit;
  cursor: pointer;
  font-weight: 600;
  user-select: none;

  &:active {
    opacity: 0.6;
  }
`;
